import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Building2, UserCog, Stethoscope, HeartPulse } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const HealthcareLogin = () => {
  const navigate = useNavigate();
  const [selectedRole, setSelectedRole] = useState('');
  const [identifier, setIdentifier] = useState('');
  const [hospitalCode, setHospitalCode] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const roles = [
    {
      id: 'admin',
      name: 'Administrator',
      icon: Building2,
      color: 'bg-blue-500',
      hoverColor: 'hover:bg-blue-600',
      route: '/admin-dashboard'
    },
    {
      id: 'frontdesk',
      name: 'Front Desk',
      icon: UserCog,
      color: 'bg-green-500',
      hoverColor: 'hover:bg-green-600',
      route: '/frontdesk-dashboard'
    },
    {
      id: 'medical-assistant',
      name: 'Medical Assistant',
      icon: HeartPulse,
      color: 'bg-pink-500',
      hoverColor: 'hover:bg-pink-600',
      route: '/medical-assistant-dashboard'
    },
    {
      id: 'doctor',
      name: 'Doctor',
      icon: Stethoscope,
      color: 'bg-purple-500',
      hoverColor: 'hover:bg-purple-600',
      route: '/doctor-dashboard'
    }
  ];

  const handleRoleSelect = (roleId) => {
    // Redirect to dedicated login pages for doctor, frontdesk, and medical assistant
    if (roleId === 'doctor') {
      navigate('/doctor-login');
      return;
    }
    if (roleId === 'frontdesk') {
      navigate('/frontdesk-login');
      return;
    }
    if (roleId === 'medical-assistant') {
      navigate('/medical-assistant-login');
      return;
    }
    // For admin, show the login form
    setSelectedRole(roleId);
    setIdentifier('');
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    
    if (!hospitalCode.trim()) {
      toast.error('Please enter hospital ID');
      return;
    }

    // Validate hospital ID format (8 characters: STATE(2) + CITY(3) + RANDOM(3))
    if (hospitalCode.trim().length !== 8) {
      toast.error('Hospital ID must be exactly 8 characters (e.g., TSHYD446)');
      return;
    }
    
    if (!identifier.trim()) {
      toast.error('Please enter your ID or email');
      return;
    }

    if ((selectedRole === 'admin' || selectedRole === 'doctor' || selectedRole === 'frontdesk') && !password.trim()) {
      const roleText = selectedRole === 'admin' ? 'administrator' : selectedRole === 'doctor' ? 'doctor' : 'frontdesk';
      toast.error(`Please enter ${roleText} password`);
      return;
    }

    setLoading(true);
    
    try {
      // First, verify hospital ID exists
      let hospital = null;
      try {
        const hospitalResponse = await axios.get(`${API}/hospitals/by-code/${hospitalCode.trim().toUpperCase()}`);
        hospital = hospitalResponse.data;
      } catch (error) {
        toast.error('Invalid hospital ID');
        setLoading(false);
        return;
      }

      let userData = null;
      let route = '';

      // For now, simple login without password
      // In the future, you can add password validation here
      
      if (selectedRole === 'admin') {
        // For admin, verify password matches hospital password
        if (identifier.toLowerCase() === 'admin' && password === hospital.password) {
          // Log successful login
          let loginId = null;
          try {
            const loginResponse = await axios.post(`${API}/security/login-event`, {
              user_id: hospital.id,
              user_name: 'Administrator',
              role: 'hospital_admin',
              hospital_id: hospital.id,
              status: 'success'
            });
            loginId = loginResponse.data.login_id;
          } catch (logError) {
            console.error('Failed to log login event:', logError);
          }
          userData = { id: 'admin', name: 'Administrator', role: 'admin', hospital_id: hospital.id, loginId };
          route = '/admin-dashboard';
        } else {
          // Log failed login attempt
          try {
            await axios.post(`${API}/security/login-event`, {
              user_id: hospital.id,
              user_name: 'Administrator',
              role: 'hospital_admin',
              hospital_id: hospital.id,
              status: 'failed',
              failure_reason: 'Invalid admin credentials'
            });
          } catch (logError) {
            console.error('Failed to log login event:', logError);
          }
          toast.error('Invalid admin credentials or password');
          setLoading(false);
          return;
        }
      } else if (selectedRole === 'frontdesk') {
        // Auto-prepend "FD" prefix if not present
        let staffId = identifier.trim();
        if (!staffId.toUpperCase().startsWith('FD')) {
          staffId = 'FD' + staffId;
        }
        
        // Try to find front desk staff by ID and hospital
        const response = await axios.get(`${API}/front-desk-staff?hospital_id=${hospital.id}`);
        const staff = response.data.find(s => s.id === staffId);
        
        if (staff) {
          // Verify password
          if (staff.password !== password) {
            toast.error('Invalid frontdesk password');
            setLoading(false);
            return;
          }
          userData = { ...staff, role: 'frontdesk' };
          route = '/frontdesk-dashboard';
        } else {
          toast.error('Front desk staff not found in this hospital');
          setLoading(false);
          return;
        }
      } else if (selectedRole === 'doctor') {
        // Auto-prepend "DOC" prefix if not present
        let doctorId = identifier.trim();
        if (!doctorId.toUpperCase().startsWith('DOC')) {
          doctorId = 'DOC' + doctorId;
        }
        
        // Try to find doctor by ID and hospital
        const response = await axios.get(`${API}/doctors?hospital_id=${hospital.id}`);
        const doctor = response.data.find(d => d.id === doctorId);
        
        if (doctor) {
          // Verify password
          if (doctor.password !== password) {
            toast.error('Invalid doctor password');
            setLoading(false);
            return;
          }
          userData = { ...doctor, role: 'doctor' };
          route = '/doctor-dashboard';
        } else {
          toast.error('Doctor not found in this hospital. Please enter your Doctor ID');
          setLoading(false);
          return;
        }
      }

      // Store user data and hospital info in localStorage
      localStorage.setItem('healthcareUser', JSON.stringify(userData));
      localStorage.setItem('currentHospital', JSON.stringify(hospital));
      
      toast.success(`Welcome to ${hospital.name}, ${userData.name}!`);
      navigate(route);
      
    } catch (error) {
      console.error('Login error:', error);
      toast.error('Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getPlaceholderText = () => {
    if (selectedRole === 'admin') return 'Enter "admin" to login';
    if (selectedRole === 'frontdesk') return 'Enter your staff ID';
    if (selectedRole === 'doctor') return 'Enter your doctor ID';
    return '';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center p-3 sm:p-4">
      <div className="max-w-4xl w-full">
        {/* Header */}
        <div className="text-center mb-4 sm:mb-6 md:mb-8">
          <div className="inline-flex items-center justify-center w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full mb-2 sm:mb-3 md:mb-4">
            <Building2 className="w-6 h-6 sm:w-7 sm:h-7 md:w-8 md:h-8 text-white" />
          </div>
          <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 mb-1 sm:mb-2" style={{fontFamily: 'Space Grotesk'}}>
            Healthcare Management System
          </h1>
          <p className="text-xs sm:text-sm md:text-base text-gray-600">Select your role to continue</p>
        </div>

        {!selectedRole ? (
          /* Role Selection */
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
            {roles.map((role) => {
              const Icon = role.icon;
              return (
                <button
                  key={role.id}
                  onClick={() => handleRoleSelect(role.id)}
                  className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 p-3 sm:p-4 md:p-6 text-center group"
                >
                  <div className={`inline-flex items-center justify-center w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 ${role.color} rounded-full mb-2 sm:mb-3 group-hover:scale-110 transition-transform`}>
                    <Icon className="w-6 h-6 sm:w-7 sm:h-7 md:w-8 md:h-8 text-white" />
                  </div>
                  <h3 className="text-sm sm:text-base md:text-lg font-bold text-gray-900 mb-1" style={{fontFamily: 'Space Grotesk'}}>
                    {role.name}
                  </h3>
                  <p className="text-xs text-gray-600 hidden sm:block">
                    {role.id === 'admin' && 'Manage hospital'}
                    {role.id === 'frontdesk' && 'Appointments'}
                    {role.id === 'medical-assistant' && 'Record vitals'}
                    {role.id === 'doctor' && 'Patient records'}
                  </p>
                </button>
              );
            })}
          </div>
        ) : (
          /* Login Form */
          <div className="bg-white rounded-2xl shadow-xl p-3 sm:p-4 md:p-6 lg:p-8 max-w-md mx-auto">
            <div className="text-center mb-3 sm:mb-4 md:mb-6">
              <div className={`inline-flex items-center justify-center w-10 h-10 sm:w-12 sm:h-12 md:w-14 md:h-14 lg:w-16 lg:h-16 ${roles.find(r => r.id === selectedRole).color} rounded-full mb-2 sm:mb-3 md:mb-4`}>
                {React.createElement(roles.find(r => r.id === selectedRole).icon, { className: "w-5 h-5 sm:w-6 sm:h-6 md:w-7 md:h-7 lg:w-8 lg:h-8 text-white" })}
              </div>
              <h2 className="text-base sm:text-lg md:text-xl lg:text-2xl font-bold text-gray-900" style={{fontFamily: 'Space Grotesk'}}>
                {roles.find(r => r.id === selectedRole).name} Login
              </h2>
            </div>

            <form onSubmit={handleLogin} className="space-y-2 sm:space-y-3 md:space-y-4 lg:space-y-5">
              <div className="space-y-1 sm:space-y-2">
                <Label htmlFor="hospitalCode" className="text-sm md:text-base">
                  Hospital ID
                </Label>
                <Input
                  id="hospitalCode"
                  type="text"
                  placeholder="Enter hospital ID (e.g., TSHYD304)"
                  value={hospitalCode}
                  onChange={(e) => setHospitalCode(e.target.value.toUpperCase().slice(0, 8))}
                  maxLength={8}
                  required
                  className="h-11 md:h-12 text-sm md:text-base"
                />
                <p className="text-xs text-gray-500">8 character code from your hospital admin</p>
              </div>

              <div className="space-y-1 sm:space-y-2">
                <Label htmlFor="identifier" className="text-sm md:text-base">
                  {selectedRole === 'admin' && 'Admin ID'}
                  {selectedRole === 'frontdesk' && 'Staff ID'}
                  {selectedRole === 'doctor' && 'Doctor ID'}
                </Label>
                <Input
                  id="identifier"
                  type="text"
                  placeholder={getPlaceholderText()}
                  value={identifier}
                  onChange={(e) => setIdentifier(e.target.value)}
                  required
                  className="h-10 sm:h-11 md:h-12 text-sm md:text-base"
                />
              </div>

              {(selectedRole === 'admin' || selectedRole === 'doctor' || selectedRole === 'frontdesk') && (
                <div className="space-y-1 sm:space-y-2">
                  <Label htmlFor="password" className="text-sm md:text-base">
                    Password
                  </Label>
                  <Input
                    id="password"
                    type="password"
                    placeholder={
                      selectedRole === 'admin' ? 'Enter hospital administrator password' : 
                      selectedRole === 'doctor' ? 'Enter your doctor password' :
                      'Enter your frontdesk password'
                    }
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="h-10 sm:h-11 md:h-12 text-sm md:text-base"
                  />
                </div>
              )}

              <div className="space-y-2 sm:space-y-3">
                <Button
                  type="submit"
                  className={`w-full h-10 sm:h-11 md:h-12 text-sm md:text-base ${roles.find(r => r.id === selectedRole).color} ${roles.find(r => r.id === selectedRole).hoverColor} text-white`}
                  disabled={loading}
                >
                  {loading ? 'Logging in...' : 'Login'}
                </Button>
                
                <Button
                  type="button"
                  variant="outline"
                  className="w-full h-10 sm:h-11 md:h-12 text-sm md:text-base"
                  onClick={() => setSelectedRole('')}
                >
                  Back to Role Selection
                </Button>
              </div>
            </form>

            <div className="mt-3 sm:mt-4 md:mt-6 pt-3 sm:pt-4 md:pt-6 border-t text-center space-y-1 sm:space-y-2">
              <p className="text-xs sm:text-sm text-gray-600">
                Parent or Guardian?{' '}
                <button
                  onClick={() => navigate('/')}
                  className="text-blue-600 hover:text-blue-700 font-medium"
                >
                  Login here
                </button>
              </p>
              <p className="text-sm text-gray-600">
                System Administrator?{' '}
                <button
                  onClick={() => navigate('/central-admin-login')}
                  className="text-purple-600 hover:text-purple-700 font-medium"
                >
                  Central Admin Portal
                </button>
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default HealthcareLogin;
